import Mixpanel from 'mixpanel';

import { CONSOLE_ICONS } from '../../constants/common';

const MIXPANEL_TOKEN = process.env.MIXPANEL_TOKEN ?? '';

let client: Mixpanel.Mixpanel | null = null;

const getClient = (): Mixpanel.Mixpanel | null => {
  if (!MIXPANEL_TOKEN) return null;
  client ??= Mixpanel.init(MIXPANEL_TOKEN, {
    geolocate: false,
  });
  return client;
};

export interface TrackingProperties {
  transform: string;
  stage: 'Success' | 'Failed';
  engine?: 'jscodeshift' | 'claude';
  repository: string;
  error?: string;
  durationMs: number;
}

/**
 * Maps internal camelCase properties to Wise Mixpanel naming conventions:
 * - Spaces between words, each word capitalized
 * - Booleans prefixed with "Is"
 * - Counts use "Number Of"
 * - Duration in seconds (not ms)
 */
const toMixpanelProperties = (
  properties: TrackingProperties,
): Record<string, string | number | boolean | undefined> => ({
  Transform: properties.transform,
  Stage: properties.stage,
  Engine: properties.engine,
  Repository: properties.repository,
  Error: properties.error,
  Duration: properties.durationMs / 1000,
});

export const TRACKING_EVENT = 'WDS - Codemod' as const;

/**
 * Fire-and-forget event tracking. Never throws — failures are silently ignored
 * so tracking never interferes with the CLI experience.
 */
export const track = (event: string, properties: TrackingProperties, debug = false): void => {
  try {
    const mapped = toMixpanelProperties(properties);

    if (debug) {
      console.debug(`${CONSOLE_ICONS.info} [Mixpanel] ${event}`, JSON.stringify(mapped, null, 2));
    }

    const mp = getClient();
    if (!mp) return;

    mp.track(event, {
      ...mapped,
    });
  } catch {
    // Intentionally swallowed — tracking must never break the CLI
  }
};
